import { useCallback, useEffect, useState } from 'react';
import { api, type BadDebtCandidate, type BadDebtReview, type BadDebtTax, type Company } from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount, date as formatDate, periodShort } from '../format';
import * as Icon from './Icons';
import { ErrorAlert, failureOf, type Failure } from './Alerts';
import type { Panel } from './Shell';

/** Klucz pozycji: dokument plus wpłata, bo powrotów korekty może być kilka na jeden dokument. */
const keyOf = (item: BadDebtCandidate) => `${item.side}-${item.documentId}-${item.paymentId ?? ''}`;

/**
 * Ulga na złe długi — w VAT (art. 89a i 89b ustawy o VAT) i w podatku dochodowym (art. 18f ustawy
 * o CIT, art. 26i ustawy o PIT).
 *
 * Ekran rozdziela **obowiązek od prawa**: korekta po stronie zakupów jest obowiązkowa (nabywca,
 * który nie zapłacił w 90 dni, musi zmniejszyć odliczenie), a po stronie sprzedaży jest wyborem
 * podatnika. Dlatego zakupy stoją pierwsze i mówią o obowiązku wprost, a sprzedaż jest propozycją.
 *
 * Oba podatki są w **jednym ekranie z przełącznikiem**, a nie w dwóch: decyzje dotyczą tych samych
 * faktur i podejmuje się je przy jednym przeglądzie. Przełącznik, a nie jedna lista, bo decyzje są
 * osobne — można skorygować VAT i zostawić dochodowy — a kwota znaczy co innego: w VAT to podatek,
 * w dochodowym kwota netto, która wchodzi do dochodu.
 *
 * Niczego nie zapisujemy samo: o wyłączeniu z art. 89b ust. 1b (dłużnik w restrukturyzacji) wie
 * tylko człowiek, więc odstąpienie od korekty **zapisujemy razem z powodem** — przy kontroli to
 * jedyne wytłumaczenie, dlaczego podatku nie skorygowano.
 */
export function BadDebtPanel({
  company,
  month,
  t,
  locale,
  editable,
  onGo,
}: {
  company: Company;
  month: string;
  t: Dictionary;
  locale: Locale;
  editable: boolean;
  onGo?: (panel: Panel) => void;
}) {
  const [tax, setTax] = useState<BadDebtTax>('VAT');
  const [review, setReview] = useState<BadDebtReview | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setReview(await api.badDebts(company.id, month, tax));
      setPicked(new Set());
    } catch (failure) {
      setReview(null);
      setError(failureOf(failure));
    }
  }, [company.id, month, tax]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  const all = [...(review?.purchases ?? []), ...(review?.sales ?? []), ...(review?.restorations ?? [])];
  const nothing = all.length === 0 && (review?.recorded.length ?? 0) === 0;
  // W VAT koryguje się podatek, w dochodowym kwotę netto — jedna kolumna, dwa znaczenia
  const kwota = (item: { net: string; vat: string }) => (tax === 'VAT' ? item.vat : item.net);
  const vat = tax === 'VAT';
  const inc = t.badDebt.income;
  const kwotaLabel = vat ? t.badDebt.vat : inc.amount;

  function toggle(item: BadDebtCandidate) {
    const key = keyOf(item);
    const next = new Set(picked);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setPicked(next);
  }

  function rows(items: BadDebtCandidate[], title: string, hint: string) {
    if (items.length === 0) return null;
    return (
      <>
        <header>
          <h5>{title}</h5>
        </header>
        <p className="sm muted">{hint}</p>
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th />
                <th>{t.badDebt.document}</th>
                <th>{t.badDebt.counterparty}</th>
                <th>{t.badDebt.dueDate}</th>
                <th>{t.badDebt.ninetiethDay}</th>
                <th className="n">{t.badDebt.unpaid}</th>
                <th className="n">{kwotaLabel}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={keyOf(item)}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`${t.badDebt.pick} ${item.documentNumber}`}
                      checked={picked.has(keyOf(item))}
                      onChange={() => toggle(item)}
                      disabled={!editable}
                    />
                  </td>
                  <td className="mono">{item.documentNumber}</td>
                  <td>{item.counterpartyName ?? '—'}</td>
                  <td className="doc">{formatDate(item.dueDate, locale)}</td>
                  <td className="doc">
                    {formatDate(item.ninetiethDay, locale)}
                    {/* Korekta należy do okresu, w którym minęło 90 dni — nie do tego, w którym ją widzimy */}
                    {item.late && (
                      <span className="chip st-warn">
                        <i />
                        {t.badDebt.late} {periodShort(item.period)}
                      </span>
                    )}
                  </td>
                  <td className="n">{amount(item.unpaid, locale)}</td>
                  <td className="n">{amount(kwota(item), locale)}</td>
                  <td className="act">
                    {editable && !item.paymentId && (
                      <button
                        type="button"
                        className="btn ghost sm"
                        disabled={busy}
                        onClick={() => {
                          const reason = window.prompt(vat ? t.badDebt.skipReason : inc.skipReason);
                          if (!reason) return;
                          void run(() =>
                            api.recordBadDebts(company.id, month, tax, [
                              { side: item.side, documentId: item.documentId, correct: false, reason },
                            ]),
                          );
                        }}
                      >
                        {t.badDebt.skip}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  return (
    <div className="panel">
      <header>
        <h4>{t.badDebt.title}</h4>
        <div className="seg" role="group" aria-label={t.badDebt.taxes}>
          {(['VAT', 'INCOME'] as BadDebtTax[]).map((option) => (
            <button
              key={option}
              type="button"
              className={option === tax ? 'on' : undefined}
              aria-pressed={option === tax}
              onClick={() => setTax(option)}
            >
              {t.badDebt.taxNames[option]}
            </button>
          ))}
        </div>
      </header>
      <p className="sm muted">{vat ? t.badDebt.lead : inc.lead}</p>

      <ErrorAlert error={error} t={t} onGo={onGo} />

      {nothing ? (
        <div className="empty">
          <Icon.Check />
          <b>{t.badDebt.none}</b>
          <p>{t.badDebt.noneHint}</p>
        </div>
      ) : (
        <>
          {rows(review?.purchases ?? [], t.badDebt.purchases, vat ? t.badDebt.purchasesHint : inc.purchasesHint)}
          {rows(
            review?.restorations ?? [],
            t.badDebt.restorations,
            vat ? t.badDebt.restorationsHint : inc.restorationsHint,
          )}
          {rows(review?.sales ?? [], t.badDebt.sales, vat ? t.badDebt.salesHint : inc.salesHint)}

          {editable && all.length > 0 && (
            <div className="row">
              <button
                type="button"
                className="btn pri"
                disabled={busy || picked.size === 0}
                onClick={() =>
                  void run(() =>
                    api.recordBadDebts(
                      company.id,
                      month,
                      tax,
                      all
                        .filter((item) => picked.has(keyOf(item)))
                        .map((item) => ({
                          side: item.side,
                          documentId: item.documentId,
                          correct: true,
                          paymentId: item.paymentId,
                        })),
                    ),
                  )
                }
              >
                {t.badDebt.apply}
              </button>
              <span className="sm muted">{vat ? t.badDebt.applyHint : inc.applyHint}</span>
            </div>
          )}

          {(review?.recorded.length ?? 0) > 0 && (
            <>
              <header>
                <h5>{t.badDebt.recorded}</h5>
              </header>
              <div className="tbl">
                <table>
                  <thead>
                    <tr>
                      <th>{t.badDebt.document}</th>
                      <th>{t.badDebt.kind}</th>
                      <th className="n">{kwotaLabel}</th>
                      <th>{t.badDebt.reason}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {review?.recorded.map((correction) => (
                      <tr key={correction.id}>
                        <td className="mono">{correction.documentNumber}</td>
                        <td>{t.badDebt.kinds[correction.kind]}</td>
                        <td className="n">{amount(kwota(correction), locale)}</td>
                        <td>{correction.reason ?? '—'}</td>
                        <td className="act">
                          {editable && (
                            <button
                              type="button"
                              className="btn danger ico sm"
                              aria-label={t.badDebt.undo}
                              title={t.badDebt.undo}
                              disabled={busy}
                              onClick={() => void run(() => api.undoBadDebt(company.id, correction.id))}
                            >
                              <Icon.Trash />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
